import { useCallback, useEffect, useState } from 'react';
import { apiModules } from '../../api/apiService';
import { Recipe } from '../../api/Recipe';

interface UseRecipeDetailsResult {
  recipe: Recipe | null;
  loading: boolean;
  error: string | null;
  reloadRecipe: (errorPrefix?: string) => Promise<Recipe | null>;
}

const buildRecipeIdMissingError = (): string => 'Aucun identifiant de recette fourni.';

const buildReloadErrorMessage = (errorPrefix: string, error: unknown): string => {
  const message = error instanceof Error ? error.message : 'Erreur inconnue';
  return `${errorPrefix}: ${message}`;
};

export const useRecipeDetails = (recipeId?: string): UseRecipeDetailsResult => {
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reloadRecipe = useCallback(async (errorPrefix = 'Erreur lors du chargement de la recette') => {
    if (!recipeId) {
      setError(buildRecipeIdMissingError());
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedRecipe = await apiModules.recipes.getRecipeById(recipeId);
      setRecipe(fetchedRecipe);
      return fetchedRecipe;
    } catch (error) {
      setError(buildReloadErrorMessage(errorPrefix, error));
      return null;
    } finally {
      setLoading(false);
    }
  }, [recipeId]);

  useEffect(() => {
    void reloadRecipe();
  }, [reloadRecipe]);

  return {
    recipe,
    loading,
    error,
    reloadRecipe,
  };
};

export default useRecipeDetails;
